import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { Shield, Loader2, Key, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

interface MFAVerificationProps {
  factorId: string;
  onVerified: () => void;
  onCancel: () => void;
}

export function MFAVerification({ factorId, onVerified, onCancel }: MFAVerificationProps) {
  const [otpCode, setOtpCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  
  // Recovery code mode
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [verifyingRecovery, setVerifyingRecovery] = useState(false);

  const handleVerify = async () => {
    if (otpCode.length !== 6) return;

    setVerifying(true);

    try {
      // Create a challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      // Verify the code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: otpCode,
      });

      if (verifyError) throw verifyError;

      onVerified();
    } catch (err: any) {
      console.error('MFA verification error:', err);
      toast.error("Invalid code", { description: "The verification code was incorrect. Please try again." });
      setOtpCode("");
    } finally {
      setVerifying(false);
    }
  };

  const handleRecoveryVerify = async () => {
    if (!recoveryCode.trim()) return;

    setVerifyingRecovery(true);

    try {
      // First verify the recovery code
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('mfa-recovery-codes', {
        body: { action: 'verify', code: recoveryCode.trim() }
      });

      if (verifyError) throw verifyError;

      if (!verifyData.valid) {
        toast.error("Invalid recovery code", { description: verifyData.error || "Invalid or already used recovery code." });
        setRecoveryCode("");
        return;
      }

      // Recovery code valid — unenroll TOTP factors so AAL1 session can proceed
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error: bypassError } = await supabase.functions.invoke('mfa-recovery-bypass', {
          body: { userId: user.id },
        });
        if (bypassError) {
          console.error('MFA bypass error:', bypassError);
          toast.error("Recovery failed", { description: "Could not disable MFA. Please contact support." });
          setRecoveryCode("");
          return;
        }
      }

      toast.success("MFA has been disabled", {
        description: "You can re-enable two-factor authentication from your settings.",
      });
      onVerified();
      
    } catch (err: any) {
      console.error('Recovery code verification error:', err);
      toast.error("Recovery failed", { description: err.message || "Failed to verify recovery code. Please try again." });
      setRecoveryCode("");
    } finally {
      setVerifyingRecovery(false);
    }
  };

  if (useRecoveryCode) {
    return (
      <div className="container py-8">
        <Card className="w-full max-w-md mx-auto shadow-elevated">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-2">
              <div className="p-3 rounded-full bg-amber-500/10">
                <Key className="h-8 w-8 text-amber-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Use Recovery Code</CardTitle>
            <CardDescription>
              Enter one of your saved recovery codes to sign in
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">

            <div className="space-y-2">
              <Input
                type="text"
                placeholder="xxxxx-xxxxx"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value)}
                className="text-center font-mono text-lg"
                autoFocus
              />
              <p className="text-xs text-muted-foreground text-center">
                Recovery codes are in the format: xxxxx-xxxxx
              </p>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={handleRecoveryVerify}
                disabled={!recoveryCode.trim() || verifyingRecovery}
                className="w-full"
              >
                {verifyingRecovery ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify Recovery Code"
                )}
              </Button>

              <Button 
                variant="ghost" 
                onClick={() => {
                  setUseRecoveryCode(false);
                  setRecoveryCode("");
                }}
                className="w-full"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to authenticator code
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <Card className="w-full max-w-md mx-auto shadow-elevated">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-2">
            <div className="p-3 rounded-full bg-primary/10">
              <Shield className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">Two-Factor Authentication</CardTitle>
          <CardDescription>
            Enter the 6-digit code from your authenticator app to continue
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={otpCode}
              onChange={setOtpCode}
              autoFocus
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={handleVerify}
              disabled={otpCode.length !== 6 || verifying}
              className="w-full"
            >
              {verifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify"
              )}
            </Button>

            <Button 
              variant="ghost" 
              onClick={onCancel}
              className="w-full"
            >
              Sign in with a different account
            </Button>
          </div>

          <div className="text-center space-y-2 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Lost access to your authenticator app?
            </p>
            <Button 
              variant="link" 
              onClick={() => {
                setUseRecoveryCode(true);
              }}
              className="text-sm"
            >
              <Key className="mr-2 h-4 w-4" />
              Use a recovery code instead
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
